import {t} from "@lingui/core/macro";
import * as React from "react";
import {call, select, takeEvery, takeLatest} from "redux-saga/effects";

import {notification} from "@atg-global-shared/notification";
import * as UserActions from "@atg-global-shared/user/userActionTypes";
import {
    ROUND_ACTIVATED,
    TEAM_MEMBER_LOGGED_IN,
    PushUtils as TillsammansPushUtils,
} from "@atg-tillsammans/push-data-access";
import {ChatActions, ChatSelectors} from "@atg-tillsammans/team-chat-data-access";
import ChatNotification from "@atg-tillsammans/team-chat-feature/components/ChatNotification/ChatNotification";
import * as chatNotificationStyles from "@atg-tillsammans/team-chat-feature/components/ChatNotification/ChatNotification.styles";
import {TeamSelectors} from "@atg-tillsammans/team-data-access/redux/team";
import {TeamService} from "@atg-tillsammans/team-data-access/services";
import {TeamMemberSelectors} from "@atg-tillsammans/team-member-data-access";
import {TeamType} from "@atg-tillsammans/types";
import {Paths} from "@atg-tillsammans-shared/navigation";
import {PersonalizationSelectors} from "@atg-tillsammans-shared/personalization-data-access";
import {isSmallScreen} from "atg-match-media/domain/matchMediaSelectors";

import LoginNotification from "../components/LoginNotification";
import * as loginNotificationStyles from "../components/LoginNotification.styles";
import RoundActivatedNotification from "../components/RoundActivatedNotification";
import * as roundActivatedNotificationStyles from "../components/RoundActivatedNotification.styles";

import {DEFAULT_CHAT_NOTIFICATIONS_OPTIONS} from "./tillsammansPushNotification";

import type {
    RoundActivatedAction,
    TeamMemberLoggedInAction,
} from "@atg-tillsammans/push-data-access";
import type {SagaIterator} from "redux-saga";

/**
 * Incommin push notification for chat messages. Used to udate cards with new message
 * indicators. Also pushes notification if enabled.
 *
 * TODO: Move this to chat push lib.
 */
export function* messageReceived(
    action: ChatActions.ChangeMessageTextAction | ChatActions.AddMessageAction,
): SagaIterator {
    const chatNotificationActivated = yield select(
        PersonalizationSelectors.isChatNotificationsActivated,
    );
    if (!chatNotificationActivated) {
        return;
    }
    const channelId = Number(action.message.channelId);
    const currentTeamId: string = yield select(TeamSelectors.getTeamId);
    const dialogOpen = yield select(ChatSelectors.isDialogOpen);
    const smallScreen = yield select(isSmallScreen);
    if (
        TillsammansPushUtils.chatNotificationAllowed(
            +currentTeamId,
            channelId,
            dialogOpen,
            smallScreen,
        )
    ) {
        const team = yield select(TeamMemberSelectors.findTeam(`${channelId}`));
        if (team && team.type === TeamType.STANDARD) {
            const chatNotification = (
                <ChatNotification
                    text={action.message.text}
                    author={action.message.user}
                    title={t({
                        id: "push.action.type.chat.message",
                        message: `${TillsammansPushUtils.capitalized(
                            action.message.user.firstName,
                        )} har skrivit i ${team.name}`,
                    })}
                    link={Paths.getTeamPagePath(`${channelId}`)}
                />
            );
            if (action.type === ChatActions.ADD_MESSAGE) {
                yield call(notification.dark, chatNotification, {
                    ...DEFAULT_CHAT_NOTIFICATIONS_OPTIONS,
                    toastId: action.message.id,
                    style: chatNotificationStyles.wrapper,
                });
            }
            if (action.type === ChatActions.CHANGE_MESSAGE_TEXT) {
                yield call(notification.update, action.message.id, {
                    render: chatNotification,
                    ...DEFAULT_CHAT_NOTIFICATIONS_OPTIONS,
                    style: chatNotificationStyles.wrapper,
                });
            }
        }
    }
}

export function* messages(
    action: ChatActions.ChangeMessageTextAction | ChatActions.AddMessageAction,
): SagaIterator {
    const memberId: number = yield select(PersonalizationSelectors.getMemberId);

    const {user, channelId} = action.message;

    if (!memberId || memberId === user.id) return;

    const currentTeamId: string = yield select(TeamSelectors.getTeamId);

    if (channelId === currentTeamId) return;

    TeamService.incrementUnreadMessages(channelId);
}

export function* messageRemoved(action: ChatActions.DeleteMessageAction): SagaIterator {
    yield call(notification.dismiss, action.messageId);
}

export function* dismissAll(): SagaIterator {
    yield call(notification.dismiss);
}

export function* roundNotification(action: RoundActivatedAction): SagaIterator {
    const memberId: number = yield select(PersonalizationSelectors.getMemberId);
    let boughtShares = 0;
    if (memberId && action.payload.subscriptions) {
        // @ts-ignore
        boughtShares = action.payload.subscriptions[memberId] || 0;
    }
    const {round, scheduledReleases} = action.payload;
    const roundActivatedNotificationsActivated = yield select(
        PersonalizationSelectors.isRoundActivatedNotificationsActivated,
    );
    const isGameInitiator =
        round.gameInitiator && memberId === round.gameInitiator.memberId;
    const showRoundNotification =
        roundActivatedNotificationsActivated && !scheduledReleases;
    if (showRoundNotification && !isGameInitiator) {
        const team = yield select(TeamMemberSelectors.findTeam(round.teamId));
        if (team)
            yield call(
                notification.dark,
                <RoundActivatedNotification
                    round={round}
                    teamName={team.name}
                    numberOfShares={boughtShares}
                />,
                {
                    ...DEFAULT_CHAT_NOTIFICATIONS_OPTIONS,
                    toastId: round.id,
                    style: roundActivatedNotificationStyles.wrapper,
                },
            );
    }
}

export function* teamMemberOnline(action: TeamMemberLoggedInAction): SagaIterator {
    const loginNotificationActivated = yield select(
        PersonalizationSelectors.isLoginNotificationsActivated,
    );
    if (!loginNotificationActivated) {
        return;
    }
    const memberId = yield select(PersonalizationSelectors.getMemberId);
    if (memberId && memberId !== action.payload.member.memberId) {
        const loginNotification = <LoginNotification member={action.payload.member} />;
        yield call(notification.dark, loginNotification, {
            ...DEFAULT_CHAT_NOTIFICATIONS_OPTIONS,
            toastId: action.payload.member.memberId,
            style: loginNotificationStyles.wrapper,
        });
    }
}

export default function* tillsammansPushNotificationSaga(): SagaIterator {
    yield takeEvery(
        [ChatActions.ADD_MESSAGE, ChatActions.CHANGE_MESSAGE_TEXT],
        messageReceived,
    );
    yield takeEvery([ChatActions.ADD_MESSAGE, ChatActions.CHANGE_MESSAGE_TEXT], messages);
    yield takeEvery(ChatActions.DELETE_MESSAGE, messageRemoved);
    yield takeLatest(UserActions.LOGOUT_INITIATED, dismissAll);

    yield takeEvery(ROUND_ACTIVATED, roundNotification);
    yield takeEvery(TEAM_MEMBER_LOGGED_IN, teamMemberOnline);
}
